import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface MaintenanceCheckerProps {
  children: React.ReactNode;
}

const TIMEOUT_MS = 5000;

const MaintenanceChecker: React.FC<MaintenanceCheckerProps> = ({ children }) => {
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageDisabled, setPageDisabled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    checkMaintenanceMode();

    // Subscribe to changes in maintenance mode
    if (supabase) {
      try {
        const channel = supabase
          .channel('site_settings_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'site_settings',
              filter: 'key=eq.maintenance_mode'
            },
            (payload) => {
              if (payload.new && typeof payload.new === 'object' && 'value' in payload.new) {
                setIsMaintenanceMode(payload.new.value as boolean);
              }
            }
          )
          .subscribe();

        return () => {
          try {
            supabase.removeChannel(channel);
          } catch (err) {
            console.warn('Error removing channel:', err);
          }
        };
      } catch (err) {
        console.warn('Error setting up realtime channel:', err);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const checkMaintenanceMode = async () => {
    timeoutRef.current = setTimeout(() => {
      console.warn('Maintenance check timed out, defaulting to non-maintenance mode');
      setIsMaintenanceMode(false);
      setLoading(false);
    }, TIMEOUT_MS);

    try {
      if (!supabase) {
        setIsMaintenanceMode(false);
        setLoading(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        return;
      }

      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error) {
        console.error('Error checking maintenance mode:', error);
        setIsMaintenanceMode(false);
      } else {
        setIsMaintenanceMode(data?.value ?? false);
      }

      // Vérifie si la page courante est désactivée (status=draft) côté admin
      if (!location.pathname.startsWith('/admin')) {
        const seg = location.pathname.replace(/^\/+/, '').split('/')[0];
        const slug = seg === '' ? 'home' : seg;
        try {
          const { data: pg } = await supabase
            .from('pages')
            .select('status')
            .eq('slug', slug)
            .maybeSingle();
          if (pg && (pg as any).status === 'draft') setPageDisabled(true);
        } catch {
          // en cas d'erreur, ne pas bloquer l'affichage
        }
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch (error) {
      console.error('Error checking maintenance mode:', error);
      setIsMaintenanceMode(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } finally {
      setLoading(false);
    }
  };

  // Don't check maintenance for admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show loading state
  if (loading && !isAdminRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Redirect to maintenance if enabled and not admin route
  if (isMaintenanceMode && !isAdminRoute) {
    return <Navigate to="/maintenance" replace />;
  }

  // Page désactivée dans l'admin (status=draft)
  if (pageDisabled && !isAdminRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-50 px-6">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-2">Page non disponible</h1>
          <p className="text-gray-600 mb-6">Cette page est momentanément désactivée. Revenez bientôt.</p>
          <a href="/" className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-6 py-3 text-sm font-bold text-white hover:bg-primary-700 transition-colors">Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default MaintenanceChecker;
